import { Container, Graphics, type Sprite } from 'pixi.js';
import type { EntityId, SimEvent, SimState } from '../../sim/state';
import { BURST_AT } from '../boss-moments';
import type { Family, RenderContext } from '../context';
import type { EnemyPose } from '../enemies';
import { FACE_RADIUS } from '../face-kit';
import type { Frame } from '../frame';
import { add, setTint } from '../util';
import { partyColors } from '../vent-mane';
import { type Point, cablePoint, drawCable } from './cable';
import { FOOT_AT, PRONG_TIPS } from './faux-contact-face';
import { BOX, FINE, paintBox } from './faux-contact-looks';
import { TRIP_BEATS, arcKinks, arcSize, flash, fuse } from './faux-contact-moments';
import { cableWhip } from './larsen-moments';
import { createBlankShow } from './faux-contact-blank';
import { onMaskPoint } from './on-mask';

const KIND = 'faux-contact';
const CABLE = { drop: 0.5, sag: 0.6, sway: 0.25, knot: 0.12 };
const BOX_HEIGHT = 1.2;
const ARC_COLOR = 0xd8f0ff;
const SPARK = { glow: 0.14, rays: 5, reach: 0.3 };
const EXIT = { beats: 2, sparks: 12, from: 0.6, reach: 2.4, dash: 0.3, width: 3 };
const TOP: readonly [number, number] = [0, -46];

interface Exit {
  readonly start: number;
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly foot: Point;
  readonly plug: Point;
  readonly box: Sprite;
}

export function createFauxContact(
  ctx: RenderContext,
  poseOf: (id: EntityId) => EnemyPose | undefined,
): Family {
  const { layers } = ctx;
  const looks = { on: paintBox(false), tripped: paintBox(true) };
  const floor = layers.dim.addChild(new Graphics());
  const props = layers.enemies.addChildAt(new Container(), 0);
  const arcs = layers.fx.addChild(new Graphics());
  const plugs = new Map<EntityId, { readonly plug: Point; readonly box: Sprite }>();
  const exits: Exit[] = [];
  const show = createBlankShow(ctx, poseOf);

  function placeBox(box: Sprite, plug: Point, radius: number, tripped: boolean, alpha: number) {
    box.texture = (tripped ? looks.tripped : looks.on).texture;
    const scale = (BOX_HEIGHT * radius) / BOX.h;
    box.scale.set(scale / FINE);
    box.position.set(plug.x, plug.y - (BOX.h / 2) * scale);
    box.alpha = alpha;
    box.visible = true;
  }

  function sparks(at: Point, radius: number, seed: number, color: number, alpha: number): void {
    arcs.circle(at.x, at.y, SPARK.glow * radius).fill({ color: 0xfff6d8, alpha });
    for (let index = 0; index < SPARK.rays; index += 1) {
      const turn = ((seed * 0.618 + index * 0.37) % 1) * Math.PI * 2;
      const reach = SPARK.reach * radius * (0.5 + ((seed + index) % 3) * 0.25);
      arcs
        .moveTo(at.x, at.y)
        .lineTo(at.x + Math.cos(turn) * reach, at.y + Math.sin(turn) * reach)
        .stroke({ width: 1.5, color, alpha });
    }
  }

  function dress(id: EntityId, pose: EnemyPose, beats: number, frame: Frame): void {
    const { radius } = pose;
    const { alpha } = pose.sprite;
    const plugged = plugs.get(id);
    if (plugged === undefined) {
      return;
    }
    const { plug, box } = plugged;
    const since = show.since(id, frame);
    const silent = since.start !== undefined && since.start >= 0 && since.end === undefined;
    const sway = frame.calm ? 0 : CABLE.sway * Math.sin(beats * 0.6);
    const bend = { drop: CABLE.drop, sag: CABLE.sag + sway, knot: CABLE.knot };
    const foot = onMaskPoint(pose, FOOT_AT);
    drawCable(floor, foot, plug, radius, bend, alpha, frame);
    placeBox(box, plug, radius, silent, alpha);
    const unit = radius / FACE_RADIUS;
    const size = arcSize(beats, since.end === undefined ? since.start : undefined, silent);
    if (size > 0) {
      PRONG_TIPS.forEach((tip, prong) => {
        const from = onMaskPoint(pose, tip);
        arcs.moveTo(from.x, from.y);
        for (const [dx, dy] of arcKinks(beats, prong, size)) {
          arcs.lineTo(from.x + dx * unit, from.y + dy * unit);
        }
        arcs.stroke({ width: 1 + 0.6 * size, color: ARC_COLOR, alpha });
      });
    }
    if (since.start === undefined || since.end !== undefined) {
      return;
    }
    const along = fuse(since.start);
    if (along !== undefined) {
      const at = cablePoint(foot, plug, radius, bend, along);
      sparks(at, radius, Math.floor(beats * 12), frame.palette.noyau, alpha);
    }
    const flashed = frame.calm ? undefined : flash(since.start);
    if (flashed !== undefined) {
      const top = onMaskPoint(pose, TOP);
      arcs
        .circle(top.x, top.y, (0.3 + 1.5 * flashed) * radius)
        .fill({ color: 0xffffff, alpha: (1 - flashed) * 0.8 });
    }
    if (since.start >= 0 && since.start < TRIP_BEATS) {
      const at = { x: plug.x, y: plug.y - (BOX_HEIGHT * radius) / 2 };
      sparks(
        at,
        radius * 1.6,
        Math.floor(beats * 12),
        frame.palette.noyau,
        1 - since.start / TRIP_BEATS,
      );
    }
  }

  function leave(exit: Exit, frame: Frame): boolean {
    const beats = (frame.show - exit.start) / frame.tempo.ticksPerBeat;
    const p = (beats - BURST_AT) / EXIT.beats;
    if (p >= 1) {
      return false;
    }
    const { foot, plug, radius, box } = exit;
    if (p < 0) {
      drawCable(floor, foot, plug, radius, { ...CABLE }, 1, frame);
      placeBox(box, plug, radius, false, 1);
      return true;
    }
    const whip = cableWhip(p);
    const end = {
      x: foot.x + (plug.x - foot.x) * whip.pull,
      y: foot.y + (plug.y - foot.y) * whip.pull,
    };
    const bend = { drop: CABLE.drop, sag: CABLE.sag * (1 + whip.lash) };
    drawCable(floor, end, plug, radius, bend, whip.alpha, frame);
    placeBox(box, plug, radius, false, 1 - p);
    if (frame.calm) {
      return true;
    }
    const party = partyColors(frame.palette);
    for (let index = 0; index < EXIT.sparks; index += 1) {
      const angle = (index / EXIT.sparks) * Math.PI * 2;
      const reach = (EXIT.from + p * EXIT.reach) * radius;
      const dash = EXIT.dash * radius;
      arcs
        .moveTo(exit.x + Math.cos(angle) * reach, exit.y + Math.sin(angle) * reach)
        .lineTo(
          exit.x + Math.cos(angle) * (reach + dash),
          exit.y + Math.sin(angle) * (reach + dash),
        )
        .stroke({
          width: EXIT.width,
          color: party[index % party.length] ?? 0xffffff,
          alpha: 1 - p,
          cap: 'round',
        });
    }
    return true;
  }

  return {
    update(state: SimState, _alpha: number, frame: Frame): void {
      floor.clear();
      arcs.clear();
      show.update(state, frame);
      const beats = frame.now / frame.tempo.ticksPerBeat;
      for (const enemy of state.enemies) {
        const pose = enemy.kind === KIND ? poseOf(enemy.id) : undefined;
        if (pose !== undefined) {
          dress(enemy.id, pose, beats, frame);
        }
      }
      for (let index = exits.length - 1; index >= 0; index -= 1) {
        const exit = exits[index];
        if (exit !== undefined && !leave(exit, frame)) {
          exit.box.destroy();
          exits.splice(index, 1);
        }
      }
    },
    onEvent(event: SimEvent, _state: SimState, frame: Frame): void {
      if (event.type === 'enemySpawned' && event.kind === KIND) {
        const box = add(props, looks.on);
        box.visible = false;
        setTint(box, 0xffffff);
        plugs.set(event.id, { plug: { x: event.x, y: event.y }, box });
      } else if (event.type === 'enemyDied' && event.kind === KIND) {
        const pose = poseOf(event.id);
        const radius = pose?.radius ?? ctx.enemyRadii?.get(KIND) ?? FACE_RADIUS;
        const plugged = plugs.get(event.id);
        plugs.delete(event.id);
        if (plugged === undefined) {
          return;
        }
        const foot =
          pose === undefined
            ? { x: event.x, y: event.y + (FOOT_AT[1] / FACE_RADIUS) * radius }
            : onMaskPoint(pose, FOOT_AT);
        exits.push({ start: frame.show, x: event.x, y: event.y, radius, foot, ...plugged });
      }
    },
    reset(): void {
      for (const { box } of [...plugs.values(), ...exits]) {
        box.destroy();
      }
      plugs.clear();
      exits.length = 0;
      show.reset();
      floor.clear();
      arcs.clear();
    },
    destroy(): void {
      show.destroy();
      for (const graphics of [floor, arcs]) {
        graphics.destroy();
      }
      props.destroy({ children: true });
      looks.on.texture.destroy(true);
      looks.tripped.texture.destroy(true);
    },
  };
}
